<template>
  <ContractReviewDisplayComponent
    :accountData="accountData"
    :contractsToShow="contracts"
    :signerFields="signerFields"
    :companyFields="companyFields"
    :generatorFields="generatorFields"
    :secondUnitFields="secondUnitFields"
    :goApplicationFields="goApplicationFields"
    :contractState="contractState"
    :hasGenerator="hasGenerator"
    @createContainerForStartSigningReady="
      $emit('createContainerForStartSigningReady', $event)
    "
  />
</template>

<script setup lang="ts">
import { formatDate } from "@/composables/formatUtils";
import { useCompanyFinder } from "@/composables/useCompanyFinder";
import { useGeneratorFinder } from "@/composables/useGeneratorFinder";
import ContractReviewDisplayComponent from "@/signup/ContractReviewDisplayComponent.vue";
import type { GeneratorFieldConfig } from "@/signup/DynamicGeneratorField.vue";
import { Account, Document } from "@/types";
import { computed, reactive } from "vue";
import { useI18n } from "vue-i18n";
import { FlowType, InvestmentSupport } from "./enums";
import { getGeneratorFields } from "./generator-fields";
import {
  type DisplayField,
  type SeGoApplication,
  type SeProductionUnit,
} from "./types";

// Define enum for contract states
enum ContractState {
  ADD_AUTHORIZATION = 1,
  ADD_FIRST_GO_PRODUCING_DEVICE = 2,
  ADD_SECOND_GO_PRODUCING_DEVICE = 3,
  ADD_EECS_CONVERSION = 4,
}

const props = withDefaults(
  defineProps<{
    accountData: Account;
    addNewDevice?: boolean;
    conversionFromNationalToEECS?: boolean;
  }>(),
  { addNewDevice: false, conversionFromNationalToEECS: false }
);

// Only createContainerForStartSigningReady is declared, as before the migration.
// disableChangingInformation is deliberately left undeclared: SESignupFlow listens for it and
// the event reaches it by fallthrough from ContractReviewDisplayComponent, which is this
// component's root node. Declaring it here without also forwarding it in the template would
// silently cut that wiring.
defineEmits<{
  createContainerForStartSigningReady: [
    createContainerForStartSigning: () => Promise<void>,
  ];
}>();

const { t } = useI18n();
const signerFields = reactive([
  { label: t("account.name"), value: props.accountData.name },
  { label: t("account.email"), value: props.accountData.email },
  {
    label: t("account.phone_number"),
    value: props.accountData.phone_number,
  },
]);
const { nullableSelectedGenerator } = useGeneratorFinder(props.accountData);
const { companyIndex } = useCompanyFinder(props.accountData);
const selectedCompany = computed(
  () => props.accountData.companies[companyIndex.value]
);

// Create helper computed properties to simplify access to nested objects
const applicationInfo = computed(
  () => nullableSelectedGenerator.value.generator?.application_information || {}
);

const companyAddress = computed(
  () => applicationInfo.value.full_company_address || {}
);

const invoiceAddress = computed(
  () => applicationInfo.value.invoice_address || null
);

/**
 * Adds technology, thermal subtype, and CHP fields to the unit fields array
 */
const addTechnologyFields = (
  unit: SeProductionUnit,
  fields: DisplayField[]
): DisplayField[] => {
  const technologyField = {
    label: t("signup.se.generator_review.technology.title"),
    value: t(
      `signup.se.generator_review.technology.options.${unit.technology}`
    ),
  };
  const updatedFields = [technologyField, ...fields];

  // Add thermal subtype if technology is Thermal
  if (unit.technology === "BTN_Thermal" && unit.thermal_subtype) {
    const thermalSubtypeField = {
      label: t("signup.se.generator_review.thermal_subtype.title"),
      value: t(
        `signup.se.generator_review.thermal_subtype.options.${unit.thermal_subtype}`
      ),
    };
    updatedFields.splice(1, 0, thermalSubtypeField);
  }

  // Add CHP field if technology is Thermal
  if (unit.technology === "BTN_Thermal") {
    const chpField = {
      label: t("signup.se.generator_review.thermal_is_chp.title"),
      value: unit.thermal_is_chp ? t("common.yes") : t("common.no"),
    };
    updatedFields.splice(unit.thermal_subtype ? 2 : 1, 0, chpField);
  }

  return updatedFields;
};

// Helper to check if a value is defined and not empty
const isValidValue = (value: any) =>
  value !== undefined && value !== "" && value !== null;

// Helper to filter out fields with undefined, empty, or null values
const hasValue = (field: { value: any }) => isValidValue(field.value);

const companyFields = computed(() => [
  { label: t("company.name"), value: selectedCompany.value.name },
  {
    label: t("company.registry_code"),
    value: selectedCompany.value.registry_code,
  },
  {
    label: t("company.address"),
    value: companyAddress.value.postal_address
      ? `${companyAddress.value.postal_address}, ${companyAddress.value.postal_code}, ${companyAddress.value.city}, ${companyAddress.value.country}`
      : selectedCompany.value.address,
  },
  {
    label: t("company.vat"),
    value: selectedCompany.value.vat || "-",
  },
  // If invoice_address exists, add all its fields
  ...(invoiceAddress.value
    ? [
        {
          label: t("signup.se.generator_review.invoice_address"),
          value: invoiceAddress.value.postal_address
            ? `${invoiceAddress.value.postal_address}, ${invoiceAddress.value.postal_code}, ${invoiceAddress.value.city}, ${invoiceAddress.value.country}`
            : "-",
        },
        // Add co_address if available
        ...(invoiceAddress.value.co_address
          ? [
              {
                label: t("signup.se.generator_review.invoice_co_address.title"),
                value: invoiceAddress.value.co_address,
              },
            ]
          : []),
        // Add specific_name if available
        ...(invoiceAddress.value.specific_name
          ? [
              {
                label: t("signup.se.generator_review.specific_name.title"),
                value: invoiceAddress.value.specific_name,
              },
            ]
          : []),
        // Add telephone if available
        ...(invoiceAddress.value.telephone
          ? [
              {
                label: t(
                  "signup.se.generator_review.invoice_contact_telephone.title"
                ),
                value: invoiceAddress.value.telephone,
              },
            ]
          : []),
        // Add email if available
        ...(invoiceAddress.value.email
          ? [
              {
                label: t("signup.se.generator_review.invoice_email.title"),
                value: invoiceAddress.value.email,
              },
            ]
          : []),
        // Add reference_number if available
        ...(invoiceAddress.value.reference_number
          ? [
              {
                label: t("signup.se.generator_review.reference_number.title"),
                value: invoiceAddress.value.reference_number,
              },
            ]
          : []),
      ]
    : // If generator exists but invoice_address is null, show that it's the same as company address
      nullableSelectedGenerator.value.generator
      ? [
          {
            label: t("signup.se.generator_review.invoice_address"),
            value: t("signup.se.generator_review.same_as_company_address"),
          },
        ]
      : []),
]);
const hasGenerator = computed(() => {
  return nullableSelectedGenerator.value.generator !== null;
});
const { productionUnitFields } = getGeneratorFields(t);

let generatorFields: DisplayField[] = [];
let secondUnitFields: DisplayField[] = [];
const goApplicationFields: DisplayField[] = [];
if (nullableSelectedGenerator.value.generator) {
  // Production unit fields - Basic Info
  generatorFields = productionUnitFields
    .filter((field: GeneratorFieldConfig) => field.title !== "technology")
    .map((field: GeneratorFieldConfig) => ({
      label: t(`signup.se.generator_review.${field.title}.title`),
      value:
        nullableSelectedGenerator.value.generator?.application_information
          ?.new_production_unit_applications?.[0]?.[field.model],
    }))
    .filter(hasValue);

  // Add technology field with human-readable value
  const firstUnit: SeProductionUnit | undefined =
    nullableSelectedGenerator.value.generator?.application_information
      ?.new_production_unit_applications?.[0];
  if (firstUnit?.technology) {
    generatorFields = addTechnologyFields(firstUnit, generatorFields);
  }

  const goApplication: SeGoApplication | undefined =
    nullableSelectedGenerator.value.generator?.application_information
      ?.go_application;
  if (goApplication) {
    // Grid Connection Info
    if (isValidValue(goApplication.grid_area_id)) {
      goApplicationFields.push({
        label: t("signup.se.generator_review.grid_area_id.title"),
        value: goApplication.grid_area_id,
      });
    }

    if (isValidValue(goApplication.grid_production_device_id)) {
      goApplicationFields.push({
        label: t("signup.se.generator_review.grid_production_device_id.title"),
        value: goApplication.grid_production_device_id,
      });
    }

    if (isValidValue(goApplication.grid_eidel_id)) {
      goApplicationFields.push({
        label: t("signup.se.generator_review.grid_eidel_id.title"),
        value: goApplication.grid_eidel_id,
      });
    }

    if (isValidValue(goApplication.grid_metered_by_hour)) {
      goApplicationFields.push({
        label: t("signup.se.generator_review.grid_metered_by_hour.title"),
        value: goApplication.grid_metered_by_hour
          ? t("common.yes")
          : t("common.no"),
      });
    }

    if (isValidValue(goApplication.grid_concession)) {
      goApplicationFields.push({
        label: t("signup.se.generator_review.grid_concession.title"),
        value: goApplication.grid_concession ? t("common.yes") : t("common.no"),
      });
    }

    // Metering Info
    if (goApplication.metering_location) {
      goApplicationFields.push({
        label: t("signup.se.generator_review.metering_location.title"),
        value: t(
          `signup.se.generator_review.metering_location.options.${goApplication.metering_location}`
        ),
      });
    }

    // Ownership Info
    if (goApplication.holding_percentage) {
      goApplicationFields.push({
        label: t("signup.se.generator_review.holding_percentage.title"),
        value: `${goApplication.holding_percentage}%`,
      });
    }

    // Investment Support Info
    if (goApplication.investment_support) {
      goApplicationFields.push({
        label: t("signup.se.generator_review.investment_support_question"),
        value: t(
          `signup.se.generator_review.investment_support_options.${goApplication.investment_support.toLowerCase()}`
        ),
      });

      if (goApplication.investment_support === InvestmentSupport.YES) {
        if (goApplication.investment_support_amount) {
          goApplicationFields.push({
            label: t(
              "signup.se.generator_review.investment_support_amount.title"
            ),
            value: `${goApplication.investment_support_amount} kr`,
          });
        }
        if (goApplication.investment_support_date) {
          goApplicationFields.push({
            label: t(
              "signup.se.generator_review.investment_support_date.title"
            ),
            value: formatDate(goApplication.investment_support_date),
          });
        }
      }
    }

    if (goApplication.grid_owner_name) {
      goApplicationFields.push({
        label: t("signup.se.generator_review.grid_owner_name.title"),
        value: goApplication.grid_owner_name,
      });
    }

    if (goApplication.grid_owner_corporate_id) {
      goApplicationFields.push({
        label: t("signup.se.generator_review.grid_owner_corporate_id.title"),
        value: goApplication.grid_owner_corporate_id,
      });
    }
  }

  // Handle second unit if exists
  if (
    nullableSelectedGenerator.value.generator?.application_information
      ?.new_production_unit_applications?.length > 1
  ) {
    secondUnitFields = productionUnitFields
      .filter((field: GeneratorFieldConfig) => field.title !== "technology")
      .map((field: GeneratorFieldConfig) => ({
        label: t(`signup.se.generator_review.${field.title}.title`),
        value:
          nullableSelectedGenerator.value.generator?.application_information
            ?.new_production_unit_applications?.[1]?.[field.model],
      }))
      .filter(hasValue);
  }

  // Add technology field for second unit if exists
  const secondUnit: SeProductionUnit | undefined =
    nullableSelectedGenerator.value.generator?.application_information
      ?.new_production_unit_applications?.[1];
  if (secondUnit?.technology) {
    secondUnitFields = addTechnologyFields(secondUnit, secondUnitFields);
  }
}

const contractState = computed(() => {
  if (!props.addNewDevice) return ContractState.ADD_AUTHORIZATION;

  // Check for conversion state using flow_type, then strict fallback:
  // Only fall back to URL flag when flow_type is not set
  const flowType =
    nullableSelectedGenerator.value.generator?.application_information
      ?.flow_type;

  const isConversion =
    flowType === FlowType.CONVERSION ||
    (flowType == null &&
      props.conversionFromNationalToEECS &&
      props.addNewDevice);

  if (isConversion) return ContractState.ADD_EECS_CONVERSION;

  return hasSignedOptionalContract("se_application_for_eecs_go")
    ? ContractState.ADD_SECOND_GO_PRODUCING_DEVICE
    : ContractState.ADD_FIRST_GO_PRODUCING_DEVICE;
});

const hasSignedOptionalContract = (selectedContract: string) => {
  return selectedCompany.value?.documents.some(
    (document: Document) =>
      document.status === "completed" && // The contract needs to be signed
      document.contract_keys.includes(selectedContract) // and if it has our contract key, we want to return true
  );
};

const shouldSignOptionalContractIfOptional = (
  optionalContractKey: string | null
): boolean => {
  return (
    !optionalContractKey || !hasSignedOptionalContract(optionalContractKey)
  );
};

const includeDocument = (
  states: ContractState[],
  optionalContractKey: string | null = null
) =>
  computed(() => {
    return (
      states.includes(contractState.value) &&
      shouldSignOptionalContractIfOptional(optionalContractKey)
    );
  });

// https://www.notion.so/solidworld/Sweden-13b259ad6d9980b99ab0ef0f79ae188f
const contracts = reactive([
  [
    {
      id: 1,
      key: "se_application_for_go",
      contract_title: t(
        "signup.contract_review_component.se.se_application_for_go_title"
      ),
      contract_description: t(
        "signup.contract_review_component.se.se_application_for_go_text"
      ),
      include: includeDocument([
        ContractState.ADD_FIRST_GO_PRODUCING_DEVICE,
        ContractState.ADD_SECOND_GO_PRODUCING_DEVICE,
        // Exclude from conversion flow - conversion users already have national GO
      ]).value,
      can_include: true,
    },
    {
      id: 2,
      key: "se_appendix_production_units",
      contract_title: t(
        "signup.contract_review_component.se.se_appendix_production_units_title"
      ),
      contract_description: t(
        "signup.contract_review_component.se.se_appendix_production_units_text"
      ),
      include: includeDocument([
        ContractState.ADD_FIRST_GO_PRODUCING_DEVICE,
        ContractState.ADD_SECOND_GO_PRODUCING_DEVICE,
        // Exclude from conversion flow - not needed for EECS conversion
      ]).value,
      can_include: true,
    },
    {
      id: 3,
      key: "se_application_for_eecs_go",
      contract_title: t(
        "signup.contract_review_component.se.se_application_for_eecs_go_title"
      ),
      contract_description: t(
        "signup.contract_review_component.se.se_application_for_eecs_go_text"
      ),
      include: includeDocument([
        ContractState.ADD_FIRST_GO_PRODUCING_DEVICE,
        ContractState.ADD_SECOND_GO_PRODUCING_DEVICE,
        ContractState.ADD_EECS_CONVERSION,
      ]).value,
      can_include: true,
    },
    {
      id: 4,
      key: "se_application_for_special_account",
      contract_title: t(
        "signup.contract_review_component.se.se_application_for_special_account_title"
      ),
      contract_description: t(
        "signup.contract_review_component.se.se_application_for_special_account_text"
      ),
      include: includeDocument([
        ContractState.ADD_FIRST_GO_PRODUCING_DEVICE,
        ContractState.ADD_EECS_CONVERSION,
      ]).value,
      can_include: true,
    },
    {
      id: 5,
      key: "se_application_for_cesar_access",
      contract_title: t(
        "signup.contract_review_component.se.se_application_for_cesar_access_title"
      ),
      contract_description: t(
        "signup.contract_review_component.se.se_application_for_cesar_access_text"
      ),
      include: includeDocument([
        ContractState.ADD_FIRST_GO_PRODUCING_DEVICE,
        // Exclude from conversion flow - conversion users already have Cesar access
      ]).value,
      can_include: true,
    },
    {
      id: 6,
      key: "se_soldera_membership_agreement",
      contract_title: t(
        "signup.contract_review_component.se.se_soldera_membership_agreement_title"
      ),
      contract_description: t(
        "signup.contract_review_component.se.se_soldera_membership_agreement_text"
      ),
      include: includeDocument(
        [
          ContractState.ADD_AUTHORIZATION,
          ContractState.ADD_FIRST_GO_PRODUCING_DEVICE,
          ContractState.ADD_EECS_CONVERSION,
        ],
        "se_soldera_membership_agreement"
      ).value,
      can_include: true,
    },
    {
      id: 7,
      key: "se_access_cesar_freja_eid_authorization_application",
      contract_title: t(
        "signup.contract_review_component.se.se_access_cesar_freja_eid_authorization_application_title"
      ),
      contract_description: t(
        "signup.contract_review_component.se.se_access_cesar_freja_eid_authorization_application_text"
      ),
      include: includeDocument(
        [
          ContractState.ADD_AUTHORIZATION,
          ContractState.ADD_FIRST_GO_PRODUCING_DEVICE,
          ContractState.ADD_EECS_CONVERSION,
        ],
        "se_access_cesar_freja_eid_authorization_application"
      ).value,
      can_include: true,
    },
  ],
]);
</script>

<style scoped lang="scss"></style>
